/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();

      // ✅ activeVariants = hidden=true without variants
      // getSelectedVariant, updateAvailability, updateAddToCartButton, updatePrice in use
      this.activeVariants = this.data.variants.filter((v) => v.hidden !== true);

      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.applySearchParams();

      // ✅ Page load on disabled option buttons hide
      this.updateDisabledOptions();

      // ✅ Page load: jો currently selected/pre-selected variant hidden hoy,
      // to first available active variant par auto-move karo
      this.selectFirstValidVariantOnLoad();

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      // ✅ Color/option change then disabled buttons re-evaluate
      this.updateDisabledOptions();

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    /**
     * ✅ Page load par: jો first selected variant hidden hoy to
     * first activeVariant na options select karo — color + size both update thay.
     */
    selectFirstValidVariantOnLoad() {
      // Jо current variant valid (active) che to koi change nahi
      if (this.variant) return;

      // First available active variant lo
      const firstValid = this.activeVariants[0];
      if (!firstValid) return;

      // Daro option selector par firstValid na corresponding option select karo
      Array.from(this.optionSelectors).forEach((selector, selectorIndex) => {
        const targetValue = firstValid.options[selectorIndex];
        if (!targetValue) return;

        if (selector.dataset.selectorType === 'dropdown') {
          const targetOpt = selector.querySelector(`.custom-select__option[data-value="${CSS.escape(targetValue)}"]`);
          if (targetOpt) {
            const customSelect = targetOpt.closest('custom-select');
            if (customSelect && customSelect.selectOption) {
              customSelect.selectOption(targetOpt);
            }
          }
        } else {
          const targetInput = selector.querySelector(`input[value="${CSS.escape(targetValue)}"]`);
          if (targetInput && !targetInput.checked) {
            targetInput.checked = true;

            // Color label text update (js-color-text)
            const colorText = selector.querySelector('.js-color-text');
            if (colorText) {
              const lbl = selector.querySelector(`label[for="${targetInput.id}"] .js-value`);
              if (lbl) colorText.textContent = lbl.textContent;
            }
          }
        }
      });

      // selectedOptions ane variant re-evaluate karo
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // UI updates
      this.updateDisabledOptions();
      this.updateAvailability();
      this.updateAddToCartButton();
      this.updatePrice();
      this.updateVariantInput();
    }

    /**
     * ✅ Hide/show COLOR and SIZE options based on hidden metafield.
     *
     * COLOR hide rule:
     *   Aa color na ALL variants (across all sizes) hidden=true hoy to → color swatch hide
     *   Ek pan size visible hoy to → color show rahe
     *
     * SIZE hide rule (color selected hoy tyare):
     *   Selected color + aa size na ALL variants hidden=true hoy to → size hide
     *   Ek pan visible variant hoy to → size show rahe
     *
     * SIZE hide rule (color selected na hoy):
     *   Aa size na ALL variants (across ALL colors) hidden=true hoy to → size hide
     *
     * No "unavailable" / "out of stock" label — directly hide karo.
     */
    updateDisabledOptions() {
      const allVariants = this.data.variants;
      const optionNames = this.data.optionNames || [];

      // ✅ Color index find karo
      const colorOptionNames = ['color', 'colour'];
      let colorIndex = -1;
      if (optionNames.length) {
        colorIndex = optionNames.findIndex(
          (n) => colorOptionNames.includes(n.toLowerCase())
        );
      }
      // DOM fallback for color index
      if (colorIndex === -1) {
        Array.from(this.optionSelectors).forEach((s, i) => {
          if (
            s.querySelector('.opt-label--image, .opt-label--swatch') !== null
            || colorOptionNames.includes((s.dataset.option || '').toLowerCase())
          ) {
            colorIndex = i;
          }
        });
      }

      // ✅ Currently selected color
      const selectedColor = colorIndex !== -1 ? this.selectedOptions[colorIndex] : null;

      Array.from(this.optionSelectors).forEach((selector) => {
        const optionName = selector.dataset.option || '';

        // ✅ Is this the color selector?
        const isColorSelector = selector.querySelector('.opt-label--image, .opt-label--swatch') !== null
          || optionName.toLowerCase() === 'color'
          || optionName.toLowerCase() === 'colour';

        // ✅ Option index find
        let optionIndex = -1;
        if (optionNames.length) {
          optionIndex = optionNames.findIndex(
            (n) => n.toLowerCase() === optionName.toLowerCase()
          );
        }
        if (optionIndex === -1) {
          optionIndex = Array.from(this.optionSelectors).findIndex(
            (s) => s.dataset.option === optionName
          );
        }
        if (optionIndex === -1) return;

        // ─── Listed (radio button) style ───
        const optionInputs = selector.querySelectorAll('input.js-option');
        optionInputs.forEach((input) => {
          const optionValue = input.value;
          let shouldHide = false;

          if (isColorSelector) {
            // ✅ COLOR RULE:
            // Aa color na ALL variants (all sizes) hidden=true hoy to → hide
            const matchingVariants = allVariants.filter(
              (v) => v.options[optionIndex] === optionValue
            );
            shouldHide = matchingVariants.length > 0
              && matchingVariants.every((v) => v.hidden === true);
          } else if (selectedColor !== null) {
            // ✅ SIZE RULE (color selected):
            // Selected color + aa size combination na ALL variants hidden=true → hide
            const matchingVariants = allVariants.filter(
              (v) => v.options[colorIndex] === selectedColor
                && v.options[optionIndex] === optionValue
            );
            shouldHide = matchingVariants.length > 0
              && matchingVariants.every((v) => v.hidden === true);
          } else {
            // ✅ SIZE RULE (no color selected):
            // Aa size na ALL variants (all colors) hidden=true → hide
            const matchingVariants = allVariants.filter(
              (v) => v.options[optionIndex] === optionValue
            );
            shouldHide = matchingVariants.length > 0
              && matchingVariants.every((v) => v.hidden === true);
          }

          const label = selector.querySelector(`label[for="${input.id}"]`);

          if (shouldHide) {
            input.style.display = 'none';
            if (label) label.style.display = 'none';
            if (input.checked) input.checked = false;
          } else {
            input.style.display = '';
            if (label) label.style.display = '';
          }
        });

        // ─── Dropdown (custom-select) style ───
        const dropdownOptions = selector.querySelectorAll('.custom-select__option[data-value]');
        dropdownOptions.forEach((opt) => {
          const optionValue = opt.dataset.value;
          if (!optionValue) return;

          let shouldHide = false;

          if (isColorSelector) {
            const matchingVariants = allVariants.filter(
              (v) => v.options[optionIndex] === optionValue
            );
            shouldHide = matchingVariants.length > 0
              && matchingVariants.every((v) => v.hidden === true);
          } else if (selectedColor !== null) {
            const matchingVariants = allVariants.filter(
              (v) => v.options[colorIndex] === selectedColor
                && v.options[optionIndex] === optionValue
            );
            shouldHide = matchingVariants.length > 0
              && matchingVariants.every((v) => v.hidden === true);
          } else {
            const matchingVariants = allVariants.filter(
              (v) => v.options[optionIndex] === optionValue
            );
            shouldHide = matchingVariants.length > 0
              && matchingVariants.every((v) => v.hidden === true);
          }

          if (shouldHide) {
            opt.style.display = 'none';
            opt.setAttribute('aria-hidden', 'true');
          } else {
            opt.style.display = '';
            opt.removeAttribute('aria-hidden');
          }
        });
      });
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');
        if (em) {
          em.hidden = available;
        }
        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     * ✅ activeVariants use — hidden=true not count available
     */
    updateAvailability() {
      if (this.dataset.availability === 'off') return;
      const { availabilityMode } = this.dataset;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // ✅ activeVariants — disabled variants availability not show
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.activeVariants.forEach((variant) => {
            let matchCount = 0;
            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });
              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown'
              ? option.dataset.value
              : option.value;
            let variantsExist = false;
            let variantsAvailable = false;

            // ✅ activeVariants use
            this.activeVariants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) variantsAvailable = true;
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;
          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        // ✅ activeVariants  lowest price
        variant = this.activeVariants[0];
        for (let i = 1; i < this.activeVariants.length; i += 1) {
          if (this.activeVariants[i].price < variant.price) variant = this.activeVariants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        if (priceWasEl) priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';

        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;
          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) from.hidden = !this.preSelection;
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant — ✅ activeVariants use, not disabled variants
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.activeVariants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
